import {CompanyProfile, CompanyProfileInputJSON, CompanyProfileOutputJSON} from '../types';

export default class CompanyProfileSerializer {
  // This function converts the company object of type CompanyProfile to an object of type CompanyProfileInputJSON
  // We are doing this because the BE is expecting properties slogan_de and slogan_en and not an object
  // slogan: { en:, de:}
  static toJsonRequest(company: CompanyProfile): CompanyProfileInputJSON {
    const override = this.toJSON(company) as CompanyProfileInputJSON;

    override.company.slogan_de = company.slogan?.de ?? '';
    override.company.slogan_en = company.slogan?.en ?? '';

    override.company.industry_de = company.industry?.de ?? '';
    override.company.industry_en = company.industry?.en ?? '';

    override.company.description_de = company.description?.de ?? '';
    override.company.description_en = company.description?.en ?? '';

    return override;
  }

  static toJSON(company: CompanyProfile): CompanyProfileOutputJSON {
    return {
      company: {
        resource_id: company.resourceId,
        name: company.name,
        slogan: {
          en: company.slogan?.en || '',
          de: company.slogan?.de || '',
        },
        website_url: company.websiteUrl,
        description: {
          en: company.description?.en || '',
          de: company.description?.de || '',
        },
        industry: {
          en: company.industry?.en || '',
          de: company.industry?.de || '',
        },
        employee_count_range: company.employeeCountRange,
        linkedin_url: company.linkedinUrl,
        xing_url: company.xingUrl,
        logo_url: company.logoUrl,
        header_image_url: company.headerImageUrl,
        company_type: company.companyType,
        average_employee_age: company.averageEmployeeAge,
        primary_company_language: company.primaryCompanyLanguage,
        work_mode: company.workMode,
        perc_of_women_in_leadership: company.percOfWomenInLeadership,
        perc_of_employees_are_parents: company.percOfEmployeesAreParents,
        locations: company.locations?.map((location) => ({
          resource_id: location.resourceId,
          street: location.street,
          house_number: location.houseNumber,
          zip_code: location.zipCode,
          city: location.city,
          additional_info: location.additionalInfo,
          country: location.country,
        })),
        benefits:
          company.benefits?.map((benefit) => ({
            resource_id: benefit.resourceId,
            category: {
              category_key: benefit.category.categoryKey,
              labels: {
                en: benefit.category.labels?.en || '',
                de: benefit.category.labels?.de || '',
              },
            },
            benefit_key: benefit.benefitKey,
            selected_at: benefit.selectedAt,
            labels: {
              en: benefit.labels?.en || '',
              de: benefit.labels?.de || '',
            },
          })) || [],
        company_media_files: {
          images:
            company.companyMediaFiles?.images?.map((image) => ({
              resource_id: image?.resourceId,
              source_url: image.sourceUrl,
              created_at: image?.createdAt,
            })) || [],
          videos:
            company.companyMediaFiles?.videos?.map((video) => ({
              resource_id: video?.resourceId,
              source_url: video.sourceUrl,
              created_at: video?.createdAt,
            })) || [],
        },
      },
    };
  }

  static toObject(payload: CompanyProfileOutputJSON): CompanyProfile {
    return {
      resourceId: payload.company.resource_id,
      name: payload.company.name,
      slogan: {
        en: payload.company.slogan?.en || '',
        de: payload.company.slogan?.de || '',
      },
      websiteUrl: payload.company.website_url,
      description: {
        en: payload.company.description?.en || '',
        de: payload.company.description?.de || '',
      },
      industry: {
        en: payload.company.industry?.en || '',
        de: payload.company.industry?.de || '',
      },
      employeeCountRange: payload.company.employee_count_range,
      linkedinUrl: payload.company.linkedin_url,
      xingUrl: payload.company.xing_url,
      logoUrl: payload.company.logo_url,
      headerImageUrl: payload.company.header_image_url,
      companyType: payload.company.company_type,
      averageEmployeeAge: payload.company.average_employee_age,
      primaryCompanyLanguage: payload.company.primary_company_language,
      workMode: payload.company.work_mode,
      percOfWomenInLeadership: payload.company.perc_of_women_in_leadership,
      percOfEmployeesAreParents: payload.company.perc_of_employees_are_parents,
      locations: payload.company.locations?.map((location) => ({
        resourceId: location.resource_id,
        street: location.street,
        houseNumber: location?.house_number,
        zipCode: location?.zip_code,
        city: location?.city,
        additionalInfo: location?.additional_info,
        country: location?.country,
      })),
      benefits: payload.company.benefits?.map((benefit) => ({
        resourceId: benefit.resource_id,
        category: {
          categoryKey: benefit.category.category_key,
          labels: {
            en: benefit.category.labels?.en || '',
            de: benefit.category.labels?.de || '',
          },
        },
        benefitKey: benefit.benefit_key,
        selectedAt: benefit.selected_at,
        labels: {
          en: benefit.labels?.en || '',
          de: benefit.labels?.de || '',
        },
      })),
      companyMediaFiles: {
        images:
          payload.company.company_media_files?.images?.map((image) => ({
            resourceId: image.resource_id,
            sourceUrl: image.source_url,
            createdAt: image.created_at,
          })) ?? [],
        videos:
          payload.company.company_media_files?.videos?.map((video) => ({
            resourceId: video.resource_id,
            sourceUrl: video.source_url,
            createdAt: video.created_at,
          })) ?? [],
      },
    };
  }
}
